export type Theme = 'light' | 'dark';

// ---------------------------------------------------------------------------
// Power AI design system - "Obsidian" (2026)
// Dark is the flagship; light is a functional equivalent.
// Tokens mirror the Obsidian mockup :root variables:
//   --bg   #0A0B0D
//   --panel / --panel2   translucent white layers
//   --line / --line2     hairlines
//   --ink / --ink2 / --ink3   text scale
//   --jade #5BD6A9 (+ dim)
// The legacy token names (surface, secondaryText, borderStrong, …) are kept as
// aliases onto the Obsidian palette so existing components don't all break;
// new components can use the first-class Obsidian names (panel, ink2, jade…).
// ---------------------------------------------------------------------------
export const BRAND_GRADIENT = 'linear-gradient(135deg, #5BD6A9 0%, #22D3EE 100%)'; // retired from rendering
export const ACCENT = '#5BD6A9';           // jade (dark flagship signal)
export const FONT_HEADING = "'Inter', system-ui, -apple-system, sans-serif";
export const FONT_BODY = "'Inter', system-ui, -apple-system, sans-serif";
export const FONT_MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// App background: two radial glows over the base, straight from the mockup .app
const DARK_APP_BG =
  'radial-gradient(900px 420px at 78% -160px, rgba(91,214,169,0.045), transparent 55%),' +
  'radial-gradient(700px 380px at 8% 110%, rgba(120,140,255,0.035), transparent 60%),' +
  '#0A0B0D';
const LIGHT_APP_BG =
  'radial-gradient(900px 420px at 78% -160px, rgba(15,157,110,0.05), transparent 55%),' +
  'radial-gradient(700px 380px at 8% 110%, rgba(90,110,220,0.045), transparent 60%),' +
  '#F7F8FA';

export const themeColors = {
  light: {
    // --- Obsidian first-class tokens (light equivalent) ---
    bg: '#F7F8FA',
    appBackground: LIGHT_APP_BG,
    panel: 'rgba(0,0,0,0.025)',
    panel2: 'rgba(0,0,0,0.05)',
    line: 'rgba(0,0,0,0.08)',
    line2: 'rgba(0,0,0,0.14)',
    ink: '#16181C',
    ink2: '#565A62',
    ink3: '#666C76',
    jade: '#0E9C6C',
    jadeDim: 'rgba(14,156,108,0.12)',
    // Jade for SMALL uppercase badge text (10-11px "TIM" / "PRIVAT" pills).
    // #0E9C6C measures 3.1:1 on the light panel, below AA for that size;
    // #0B7E57 is 4.6:1 and still reads as the same accent.
    jadeStrong: '#0B7E57',
    mono: FONT_MONO,
    composerBg: 'rgba(255,255,255,0.9)',
    composerShadow: '0 20px 44px -24px rgba(0,0,0,0.28)',
    // Brand mark chip (sidebar + assistant byline) - must flip with the theme
    logoSrc: '/logo-only200-removebg.png',
    markBg: 'linear-gradient(140deg,#FFFFFF,#EDEFF2)',
    markGlow: '0 1px 3px rgba(0,0,0,0.10)',

    // --- Legacy aliases (mapped onto Obsidian) ---
    background: '#F7F8FA',
    surface: 'rgba(0,0,0,0.025)',
    surface2: 'rgba(0,0,0,0.05)',
    text: '#16181C',
    secondaryText: '#565A62',
    mutedText: '#6E747D',
    border: 'rgba(0,0,0,0.08)',
    borderStrong: 'rgba(0,0,0,0.14)',
    accent: '#0E9C6C',
    accentSoft: 'rgba(14,156,108,0.12)',
    accentBorder: 'rgba(14,156,108,0.30)',
    gradient: BRAND_GRADIENT,
    gradientOnText: '#06251C',
    focusGlow: '0 0 0 3px rgba(14,156,108,0.10)',
    ring: 'rgba(14,156,108,0.5)',
    // Primary (monochrome) buttons - dark button on light theme
    btnPrimaryBg: '#16181C',
    btnPrimaryText: '#FFFFFF',
    btnPrimaryHover: '#000000',
    shadow: '0 20px 44px -24px rgba(0,0,0,0.28)',
    shadowSoft: '0 1px 3px rgba(0,0,0,0.08)',
    messageBackground: {
      user: 'rgba(0,0,0,0.05)',
      assistant: 'transparent'
    },
    inputBackground: 'rgba(255,255,255,0.9)',
    sidebarBackground: 'transparent',
    hoverBackground: 'rgba(0,0,0,0.025)',
    selectedBackground: 'rgba(0,0,0,0.05)',
    headingText: '#16181C',
    codeBg: 'rgba(0,0,0,0.04)',
  },
  dark: {
    // --- Obsidian first-class tokens (flagship) ---
    bg: '#0A0B0D',
    appBackground: DARK_APP_BG,
    panel: 'rgba(255,255,255,0.025)',
    panel2: 'rgba(255,255,255,0.05)',
    line: 'rgba(255,255,255,0.06)',
    line2: 'rgba(255,255,255,0.12)',
    ink: '#F2F3F5',
    ink2: '#9FA4AD',
    // ink3 is the instructional/meta tier. #5C6068 measured 3.12:1 on #0A0B0D
    // (below WCAG AA for small text); #797F88 is exactly 4.5:1 on the same base.
    ink3: '#797F88',
    jade: '#5BD6A9',
    jadeDim: 'rgba(91,214,169,0.12)',
    // Dark theme already clears AA at 10px, so the badge tone is the base jade.
    jadeStrong: '#5BD6A9',
    mono: FONT_MONO,
    composerBg: 'rgba(18,20,24,0.85)',
    composerShadow: '0 24px 48px -20px rgba(0,0,0,0.7)',
    // Brand mark chip (sidebar + assistant byline) - must flip with the theme
    logoSrc: '/logo-only200-removebg-inverted.png',
    markBg: 'linear-gradient(140deg,#20242B,#101216)',
    markGlow: '0 0 10px rgba(91,214,169,0.25)',

    // --- Legacy aliases (mapped onto Obsidian) ---
    background: '#0A0B0D',
    surface: 'rgba(255,255,255,0.025)',
    surface2: 'rgba(255,255,255,0.05)',
    text: '#F2F3F5',
    secondaryText: '#9FA4AD',
    mutedText: '#5C6068',
    border: 'rgba(255,255,255,0.06)',
    borderStrong: 'rgba(255,255,255,0.12)',
    accent: '#5BD6A9',
    accentSoft: 'rgba(91,214,169,0.12)',
    accentBorder: 'rgba(91,214,169,0.30)',
    gradient: BRAND_GRADIENT,
    gradientOnText: '#06251C',
    focusGlow: '0 0 0 3px rgba(91,214,169,0.08)',
    ring: 'rgba(91,214,169,0.5)',
    // Primary (monochrome) buttons - light button on dark theme
    btnPrimaryBg: '#F2F3F5',
    btnPrimaryText: '#0B0C0E',
    btnPrimaryHover: '#FFFFFF',
    shadow: '0 24px 48px -20px rgba(0,0,0,0.7)',
    shadowSoft: '0 1px 3px rgba(0,0,0,0.4)',
    messageBackground: {
      user: 'rgba(255,255,255,0.05)',
      assistant: 'transparent'
    },
    inputBackground: 'rgba(18,20,24,0.85)',
    sidebarBackground: 'transparent',
    hoverBackground: 'rgba(255,255,255,0.025)',
    selectedBackground: 'rgba(255,255,255,0.05)',
    headingText: '#F2F3F5',
    codeBg: 'rgba(255,255,255,0.03)',
  }
};
